import {KitchenService} from '../../../../../services/KitchenService/KitchenService';
import {CommonHelper} from 'common-code';
import {EllipseCurve} from 'three';
import {ThreeBottomUnit} from '../ThreeBottomUnit';
import {ISaveBarCounterSaveData} from '../../../../../../../common-code/interfaces/saveData/ISaveBarCounterSaveData';
import {
    ISaveBarCounterCorpusData
} from '../../../../../../../common-code/interfaces/saveData/ISaveBarCounterCorpusData';
import {ThreeBarCounterCorpus} from '../../../details/ThreeCorpus/types/ThreeBarCounterCorpus';
import {ISaveKUnitDetailData} from '../../../../../../../common-code/interfaces/saveData/ISaveKUnitDetailData';
import {TBarCounterWallDirection} from '../../../../../../../common-code/types/barCounter/TBarCounterWallDirection';
import {
    ALIGN_LEFT, TABLETOP_END_TYPE_CIRCLE, TABLETOP_END_TYPE_DEFAULT,
    TABLETOP_END_TYPE_HALF_CIRCLE_LEFT, TABLETOP_END_TYPE_HALF_CIRCLE_RIGHT,
    WALL_DIRECTION_PARALLEL,
    WALL_DIRECTION_PERPENDICULAR
} from '../../../../../../../common-code/constants';
import {TBarCounterLegPosition} from '../../../../../../../common-code/types/barCounter/TBarCounterLegPosition';
import {TTabletopEndType} from '../../../../../../../common-code/types/barCounter/TTabletopEndType';
import {TPoint2D} from '../../../../../../../common-code/types/TPoint2D';

export class ThreeBottomUnitBarCounter extends ThreeBottomUnit {
    saveData: ISaveBarCounterSaveData;
    corpus: ThreeBarCounterCorpus;

    constructor(options: ISaveBarCounterSaveData, service: KitchenService) {
        super(options, service);
        this.saveData = options;
        this.corpus = this.initCorpus(options.corpus);
    }

    public getWallDirection(): TBarCounterWallDirection {
        return this.saveData.wallDirection || WALL_DIRECTION_PERPENDICULAR;
    }

    public getLegPosition(): TBarCounterLegPosition {
        return this.saveData.legPosition || ALIGN_LEFT;
    }

    public getLegModelId(): string | undefined {
        return this.saveData.legModelId;
    }

    public tabletopLeftEnd(): TTabletopEndType {
        return this.saveData.tabletopLeftEnd || TABLETOP_END_TYPE_DEFAULT;
    }

    public tabletopRightEnd(): TTabletopEndType {
        return this.saveData.tabletopRightEnd || TABLETOP_END_TYPE_DEFAULT;
    }

    protected initCorpus(corpusData: ISaveBarCounterCorpusData): ThreeBarCounterCorpus {
        return new ThreeBarCounterCorpus(CommonHelper.deepCopy(corpusData), this);
    }

    protected initTabletopsData(tabletops?: ISaveKUnitDetailData[]): ISaveKUnitDetailData[] | undefined {
        if (tabletops) {
            for (const tabletop of tabletops) {
                switch (this.getWallDirection()) {
                    case WALL_DIRECTION_PARALLEL:
                        if (!tabletop.rotation) {
                            tabletop.rotation = {};
                        }
                        tabletop.rotation.y = 0;
                        if (!tabletop.sizes) {
                            tabletop.sizes = {};
                        }
                        tabletop.sizes.length = this.getWidth();
                        tabletop.sizes.width = this.getDepth();
                        break;
                    case WALL_DIRECTION_PERPENDICULAR:
                    default:
                        if (!tabletop.rotation) {
                            tabletop.rotation = {};
                        }
                        tabletop.rotation.y = Math.PI / 2;
                        if (!tabletop.sizes) {
                            tabletop.sizes = {};
                        }
                        tabletop.sizes.length = this.getWidth();
                        tabletop.sizes.width = this.getDepth();
                        break;
                }
                tabletop.position = {x: 0, z: 0};
                switch (this.tabletopLeftEnd()) {
                    case TABLETOP_END_TYPE_CIRCLE:
                        tabletop.leftPoints = this.calculateCircleTabletopPoints(tabletop.sizes.width);
                        break;
                    case TABLETOP_END_TYPE_HALF_CIRCLE_RIGHT:
                        tabletop.leftPoints = this.calculateHalfCircleRightTabletopPoints(tabletop.sizes.width);
                        break;
                    case TABLETOP_END_TYPE_HALF_CIRCLE_LEFT:
                        tabletop.leftPoints = this.calculateHalfCircleLeftTabletopPoints(tabletop.sizes.width);
                        break;
                }
                switch (this.tabletopRightEnd()) {
                    case TABLETOP_END_TYPE_CIRCLE:
                        tabletop.rightPoints = this.calculateCircleTabletopPoints(tabletop.sizes.width);
                        break;
                    case TABLETOP_END_TYPE_HALF_CIRCLE_RIGHT:
                        tabletop.rightPoints = this.calculateHalfCircleRightTabletopPoints(tabletop.sizes.width);
                        break;
                    case TABLETOP_END_TYPE_HALF_CIRCLE_LEFT:
                        tabletop.rightPoints = this.calculateHalfCircleLeftTabletopPoints(tabletop.sizes.width);
                        break;
                }
            }
        }

        return tabletops;
    }

    protected calculateCircleTabletopPoints(tabletopDepth: number): TPoint2D[] {
        let points: TPoint2D[] = [];
        let curve;

        curve = new EllipseCurve(
            0, 0, tabletopDepth / 2, tabletopDepth / 2,
            -0.5 * Math.PI, 0.5 * Math.PI, false, 0
        );
        points.push({x: 0, y: -tabletopDepth / 2});
        this.setCurveTabletopPoints(curve, points);
        points.push({x: 0, y: tabletopDepth / 2});

        return points;
    }

    protected calculateHalfCircleLeftTabletopPoints(tabletopDepth: number): TPoint2D[] {
        let points: TPoint2D[] = [];
        let curve;

        curve = new EllipseCurve(
            0, tabletopDepth / 2, tabletopDepth, tabletopDepth,
            -0.5 * Math.PI, 0, false, 0
        );
        points.push({x: 0, y: -tabletopDepth / 2});
        this.setCurveTabletopPoints(curve, points);
        points.push({x: tabletopDepth / 2, y: tabletopDepth / 2});

        return points;
    }

    protected calculateHalfCircleRightTabletopPoints(tabletopDepth: number): TPoint2D[] {
        let points: TPoint2D[] = [];
        let curve;

        curve = new EllipseCurve(
            0, -tabletopDepth / 2, tabletopDepth, tabletopDepth,
            0, 0.5 * Math.PI, false, 0
        );
        points.push({x: tabletopDepth / 2, y: -tabletopDepth / 2});
        this.setCurveTabletopPoints(curve, points);
        points.push({x: 0, y: tabletopDepth / 2});

        return points;
    }

    protected setCurveTabletopPoints(curve: EllipseCurve, points: TPoint2D[]) {
        let curvePoints;
        let index;

        curvePoints = curve.getSpacedPoints(100);
        for (index in curvePoints) {
            if (+index === 0 || +index === curvePoints.length - 1) {
                continue;
            }
            if (curvePoints.hasOwnProperty(index)) {
                points.push({x: curvePoints[index].x, y: curvePoints[index].y});
            }
        }
    }
}